import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  IconButton,
  Box,
  Alert,
  Typography,
  FormHelperText,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { pb, getCurrentUser } from "../services/pocketbase";

// Add the formatDiscordMarkdown function
const formatDiscordMarkdown = (text) => {
  if (!text) return "";

  // Process complex formats first
  // Underline Bold Italic: __***text***__
  text = text.replace(
    /__\*\*\*(.*?)\*\*\*__/g,
    "<u><strong><em>$1</em></strong></u>"
  );

  // Underline Bold: __**text**__
  text = text.replace(/__\*\*(.*?)\*\*__/g, "<u><strong>$1</strong></u>");

  // Underline Italic: __*text*__
  text = text.replace(/__\*(.*?)\*__/g, "<u><em>$1</em></u>");

  // Bold Italic: ***text***
  text = text.replace(/\*\*\*(.*?)\*\*\*/g, "<strong><em>$1</em></strong>");

  // Then process simpler formats
  // Bold: **text**
  text = text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");

  // Italic: *text* or _text_
  text = text.replace(/\*(.*?)\*|_(.*?)_/g, "<em>$1$2</em>");

  // Underline: __text__
  text = text.replace(/__(.*?)__/g, "<u>$1</u>");

  // Strikethrough: ~~text~~
  text = text.replace(/~~(.*?)~~/g, "<s>$1</s>");

  // Code blocks: ```text```
  text = text.replace(/```(.*?)```/gs, "<pre><code>$1</code></pre>");

  // Inline code: `text`
  text = text.replace(/`(.*?)`/g, "<code>$1</code>");

  // Line breaks
  text = text.replace(/\n/g, "<br />");

  return text;
};

const FAQEditor = ({ open, onClose, faq = null }) => {
  const [formData, setFormData] = useState({
    question: "",
    answer: "",
    position: 0,
  });
  const [error, setError] = useState(null);
  const user = getCurrentUser();

  useEffect(() => {
    if (faq) {
      setFormData({
        question: faq.question || "",
        answer: faq.answer || "",
        position: faq.position || 0,
      });
    } else {
      // For new FAQs, set the position to the end
      setFormData({
        question: "",
        answer: "",
        position: 999, // Will be updated with the correct position on save
      });
    }
  }, [faq]);

  const sendWebhookNotification = async (action, newData, oldData = null) => {
    try {
      const timestamp = new Date().toISOString();
      let webhookData = {
        content: "",
        embeds: []
      };

      if (action === "create") {
        webhookData = {
          content: "New FAQ Created",
          embeds: [
            {
              title: "New FAQ Created",
              color: 0x00ff00,
              fields: [
                {
                  name: "Question",
                  value: newData.question
                },
                {
                  name: "Answer",
                  value: newData.answer
                },
                {
                  name: "Created By",
                  value: user?.username || "Unknown User"
                }
              ],
              timestamp: timestamp
            }
          ]
        };
      } else if (action === "edit") {
        webhookData = {
          content: "FAQ Edited",
          embeds: [
            {
              title: "FAQ Edited",
              color: 0xffaa00,
              fields: [
                {
                  name: "Original Question",
                  value: oldData.question
                },
                {
                  name: "New Question",
                  value: newData.question
                },
                {
                  name: "Original Answer",
                  value: oldData.answer
                },
                {
                  name: "New Answer",
                  value: newData.answer
                },
                {
                  name: "Edited By",
                  value: user?.username || "Unknown User"
                }
              ],
              timestamp: timestamp
            }
          ]
        };
      }

      // Send webhook notification
      await fetch("/api/webhooks/faq-logs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookData),
      });
    } catch (error) {
      console.error("Error sending webhook notification:", error);
      // Don't block the main operation if webhook fails
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    try {
      const data = {
        question: formData.question,
        answer: formData.answer,
        position: formData.position,
      };

      if (faq?.id) {
        // Update existing FAQ
        await pb.collection("faqs").update(faq.id, data);
        // Send webhook notification for edit
        await sendWebhookNotification("edit", data, faq);
      } else {
        // For new FAQs, get the highest position and add 1
        try {
          const records = await pb.collection("faqs").getList(1, 1, {
            sort: "-position",
          });

          if (records.items.length > 0) {
            data.position = records.items[0].position + 1;
          } else {
            data.position = 1;
          }
        } catch (err) {
          console.error("Error getting highest position:", err);
          data.position = 1;
        }

        // Create new FAQ
        await pb.collection("faqs").create(data);
        // Send webhook notification for create
        await sendWebhookNotification("create", data);
      }

      onClose(true);
    } catch (err) {
      console.error("Error saving FAQ:", err);
      setError(err.message || "Failed to save FAQ");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => onClose(false)}
      maxWidth="md"
      sx={{ zIndex: 99 }}
      fullWidth
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        {faq ? "Edit FAQ" : "Add FAQ"}
        <IconButton
          aria-label="close"
          onClick={() => onClose(false)}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              fullWidth
              label="Question"
              value={formData.question}
              onChange={(e) =>
                setFormData({ ...formData, question: e.target.value })
              }
              required
            />

            <Box>
              <TextField
                fullWidth
                label="Answer"
                value={formData.answer}
                onChange={(e) =>
                  setFormData({ ...formData, answer: e.target.value })
                }
                multiline
                rows={6}
                required
              />
              <FormHelperText>
                Use Discord-style markdown: **bold**, *italic*, ***bold
                italic***, __underline__, __*underline italic*__, __**underline
                bold**__, __***underline bold italic***__, ~~strikethrough~~,
                `inline code`, ```code blocks```
              </FormHelperText>

              {formData.answer && (
                <Box
                  sx={{
                    mt: 2,
                    p: 2,
                    border: "1px solid #ccc",
                    borderRadius: 1,
                  }}
                >
                  <Typography variant="subtitle2" gutterBottom>
                    Preview:
                  </Typography>
                  <Box
                    sx={{
                      whiteSpace: "pre-wrap",
                      "& code": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "2px 4px",
                        borderRadius: "3px",
                        fontFamily: "monospace",
                      },
                      "& pre": {
                        backgroundColor: "rgba(0, 0, 0, 0.1)",
                        padding: "8px",
                        borderRadius: "4px",
                        overflowX: "auto",
                      },
                      "& strong": {
                        fontWeight: "bold",
                      },
                      "& em": {
                        fontStyle: "italic",
                      },
                      "& u": {
                        textDecoration: "underline",
                      },
                      "& s": {
                        textDecoration: "line-through",
                      },
                    }}
                    dangerouslySetInnerHTML={{
                      __html: formatDiscordMarkdown(formData.answer),
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button type="submit" variant="contained">
            Save
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default FAQEditor;
